
import { computed, onMounted, reactive, ref } from 'vue';
import { makeRequest, METHODS } from '@/utils/apiUtils';
import { canchasState } from '@/store/mantenedores.store';
import UITextSelect from '@/components/UI/UITextSelect.vue';
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';
import { userState } from '@/store/user.store';
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

interface StockEnRumaDaysQty {
  menosDe10: number;
  entre10y15: number;
  entre16y30: number;
  entre31y60: number;
  entre61y90: number;
  entre91y120: number;
  entre121y150: number;
  entre151y180: number;
  entre181y210: number;
  entre211y240: number;
  entre241y270: number;
  entre271y360: number;
  sobre360: number;
}

interface StockEnRumaMonthsQty {
  menosDe1Mes: number;
  entre1y2Meses: number;
  entre2y3Meses: number;
  entre3y4Meses: number;
  entre4y5Meses: number;
  entre5y6Meses: number;
  entre6y7Meses: number;
  entre7y8Meses: number;
  entre8y9Meses: number;
  entre9y10Meses: number;
  entre10y11Meses: number;
  entre11y12Meses: number;
  sobre12Meses: number;
}

interface StockEnRumaEspecie {
  id: string;
  descripcion: string;
}

interface StockEnRumaPorAntiguedadItem {
  especie: StockEnRumaEspecie;
  days: StockEnRumaDaysQty;
  months: StockEnRumaMonthsQty;
}

export default {
  name: 'Graph1',
  components: {
    UITextSelect,
    Bar
  },
  setup() {
    const selectedCancha = ref<string | null>(null);
    const DAYS = 'days' as const;
    const MONTHS = 'months' as const;
    let mode: string = MONTHS;
    const colors = [
      '#CD5C5C',
      '#008000',
      '#800080',
      '#FF0000',
      '#000080',
      '#FA8072',
      '#FF00FF',
      '#0000FF',
      '#00FFFF',
      '#808000',
      '#FFFF00',
      '#CD5C5C',
      '#008000',
      '#FA8072',
      '#800080',
      '#FF0000',
      '#000080',
      '#FF00FF',
      '#0000FF',
      '#00FFFF',
      '#808000',
      '#FFFF00'
    ];
    const user = computed(() => userState.value);
    const hasStatsData = ref(false);
    const isLoading = ref(false);
    const items = ref<{ text: string; value: number }[]>([]);
    const canchas = ref<{ id: string; label: string }[]>([]);
    const selectedCanchaName = computed(() => {
      if (!selectedCancha.value) return '';
      const cancha = canchas.value.find((c) => c.id === selectedCancha.value);
      return cancha ? cancha.label : '';
    });
    const chartData = reactive<{ labels: string[]; datasets: { label: string; backgroundColor: string; data: number[] }[] }>({
      labels: [],
      datasets: []
    });
    const requestStats = async (id?: string) => {
      function formatNum(num: number) {
        return num ? +(num as number).toFixed(2) : 0;
      }
      if (!id) {
        selectedCancha.value = null;
        hasStatsData.value = false;
        return;
      }
      selectedCancha.value = id;
      try {
        isLoading.value = true;
        const url = `stats/stock-en-ruma-por-antiguedad?cancha=${id}`;
        const response = await makeRequest<StockEnRumaPorAntiguedadItem[]>(METHODS.GET, url, true);

        if (!response.payload || !response.payload.length) {
          hasStatsData.value = false;
          return;
        }

        hasStatsData.value = true;

        if (mode === DAYS) {
          chartData.labels = [
            'Menos de 10 días',
            'Entre 10 y 15 días',
            'Entre 16 y 30 días',
            'Entre 31 y 60 días',
            'Entre 61 y 90 días',
            'Entre 91 y 120 días',
            'Entre 121 y 150 días',
            'Entre 151 y 180 días',
            'Entre 181 y 210 días',
            'Entre 211 y 240 días',
            'Entre 241 y 270 días',
            'Entre 271 y 360 días',
            'Sobre 360 días'
          ];
        } else {
          chartData.labels = [
            'Menos de 1 mes',
            'Entre 1 y 2 meses',
            'Entre 2 y 3 meses',
            'Entre 3 y 4 meses',
            'Entre 4 y 5 meses',
            'Entre 5 y 6 meses',
            'Entre 6 y 7 meses',
            'Entre 7 y 8 meses',
            'Entre 8 y 9 meses',
            'Entre 9 y 10 meses',
            'Entre 10 y 11 meses',
            'Entre 11 y 12 meses',
            'Sobre 12 meses'
          ];
        }

        chartData.datasets = [];

        response.payload.forEach((el: StockEnRumaPorAntiguedadItem, i: number) => {
          const data =
            mode === DAYS
              ? [
                  formatNum(el.days.menosDe10),
                  formatNum(el.days.entre10y15),
                  formatNum(el.days.entre16y30),
                  formatNum(el.days.entre31y60),
                  formatNum(el.days.entre61y90),
                  formatNum(el.days.entre91y120),
                  formatNum(el.days.entre121y150),
                  formatNum(el.days.entre151y180),
                  formatNum(el.days.entre181y210),
                  formatNum(el.days.entre211y240),
                  formatNum(el.days.entre241y270),
                  formatNum(el.days.entre271y360),
                  formatNum(el.days.sobre360)
                ]
              : [
                  formatNum(el.months.menosDe1Mes),
                  formatNum(el.months.entre1y2Meses),
                  formatNum(el.months.entre2y3Meses),
                  formatNum(el.months.entre3y4Meses),
                  formatNum(el.months.entre4y5Meses),
                  formatNum(el.months.entre5y6Meses),
                  formatNum(el.months.entre6y7Meses),
                  formatNum(el.months.entre7y8Meses),
                  formatNum(el.months.entre8y9Meses),
                  formatNum(el.months.entre9y10Meses),
                  formatNum(el.months.entre10y11Meses),
                  formatNum(el.months.entre11y12Meses),
                  formatNum(el.months.sobre12Meses)
                ];
          chartData.datasets.push({
            label: el.especie.descripcion,
            backgroundColor: colors[i],
            data
          });
        });
      } catch (error) {
        console.log(error);
      } finally {
        isLoading.value = false;
      }
    };
    onMounted(() => {
      if (user.value?.cancha !== 'ALL') {
        canchas.value = canchasState.value
          .filter((el) => el._id === user.value?.cancha)
          .map((el) => ({ id: el._id, label: el.codigo + ' ' + el.descripcion }));
      } else {
        canchas.value = canchasState.value.map((el) => ({ id: el._id, label: el.codigo + ' ' + el.descripcion }));
      }
    });

    return {
      canchas,
      requestStats,
      hasStatsData,
      isLoading,
      items,
      chartData,
      selectedCancha,
      selectedCanchaName
    };
  }
};
